import moment from "moment-timezone";

export type SectorMomentumDataItem = {
  rsi_weekly: number | string;
  week: string;
};

export type SectorMomentumData = Record<string, SectorMomentumDataItem[]>;

export function getSectorMomentumDateRange(selectedDate: string): {
  fromDate: string;
  toDate: string;
} {
  const today = moment.tz("Asia/Kolkata").startOf("day");
  const selected = moment
    .tz(selectedDate, "YYYY-MM-DD", "Asia/Kolkata")
    .startOf("day");
  const sevenWeeksAgo = today.clone().subtract(7, "weeks");

  if (selected.isBefore(sevenWeeksAgo)) {
    return {
      fromDate: selected.clone().subtract(1, "week").format("YYYY-MM-DD"),
      toDate: selected.clone().add(5, "weeks").format("YYYY-MM-DD"),
    };
  }

  return {
    fromDate: selected.clone().subtract(6, "weeks").format("YYYY-MM-DD"),
    toDate: selected.format("YYYY-MM-DD"),
  };
}

export function getSectorMomentumDateRangeDates(selectedDate: Date): {
  start: Date;
  end: Date;
} {
  const formatted = moment(selectedDate).format("YYYY-MM-DD");
  const { fromDate, toDate } = getSectorMomentumDateRange(formatted);

  return {
    start: moment.tz(fromDate, "YYYY-MM-DD", "Asia/Kolkata").toDate(),
    end: moment.tz(toDate, "YYYY-MM-DD", "Asia/Kolkata").toDate(),
  };
}

export function formatSectorMomentumWeek(date: string): string {
  const m = moment.tz(date, "YYYY-MM-DD", "Asia/Kolkata");
  return `${m.isoWeekYear()} Wk-${m.isoWeek()}`;
}

export function getSectorMomentumHighlightWeek(
  rankingData: SectorMomentumData,
  selectedDate?: string,
): string | null {
  const sectors = Object.keys(rankingData);
  if (sectors.length === 0) return null;

  const weeks = rankingData[sectors[0]]?.map((item) => item.week) ?? [];
  if (weeks.length === 0) return null;

  if (selectedDate) {
    const targetWeek = formatSectorMomentumWeek(selectedDate);
    if (weeks.includes(targetWeek)) return targetWeek;
  }

  return weeks[weeks.length - 1] ?? null;
}

export function parseRsiWeekly(value: number | string | undefined): number {
  if (value === undefined || value === null) return 0;
  const parsed = typeof value === "number" ? value : parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function computeSectorRanks(
  data: SectorMomentumData,
): Record<string, number[]> {
  const sectorRanks: Record<string, number[]> = {};

  if (!data || Object.keys(data).length === 0) {
    return sectorRanks;
  }

  const firstKey = Object.keys(data)[0];
  const weekCount = data[firstKey]?.length || 0;

  for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
    const weekValues = Object.entries(data)
      .map(([sector, values]) => ({
        sector,
        value: parseRsiWeekly(values[weekIndex]?.rsi_weekly),
      }))
      .filter((d) => d.value !== undefined);

    weekValues.sort((a, b) => b.value - a.value);

    weekValues.forEach((item, rank) => {
      if (!sectorRanks[item.sector]) sectorRanks[item.sector] = [];
      sectorRanks[item.sector][weekIndex] = rank + 1;
    });
  }

  return sectorRanks;
}

export function getOrderedSectorsByLastWeek(
  rankedData: Record<string, number[]>,
): string[] {
  if (!rankedData || Object.keys(rankedData).length === 0) {
    return [];
  }

  const firstKey = Object.keys(rankedData)[0];
  const lastWeekIndex = (rankedData[firstKey]?.length || 1) - 1;

  return Object.keys(rankedData).sort(
    (a, b) =>
      (rankedData[a]?.[lastWeekIndex] ?? Infinity) -
      (rankedData[b]?.[lastWeekIndex] ?? Infinity),
  );
}
